// ============================================================
// FILE: src/pages/Services.jsx
// ============================================================

import React, { useState, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
    Building2,
    Factory,
    Hammer,
    HardHat,
    Settings,
    ShieldCheck,
    ArrowRight,
    CheckCircle2,
    Sparkles,
    Ruler,
    Layers3,
    Clock3,
} from "lucide-react";

// ============================================================
// SERVICES DATA
// ============================================================

const services = [
    {
        number: "06",
        icon: ShieldCheck,
        category: "Tensile Strength",
        title: "Tensile",
        shortTitle: "Tensile Strength",
        description:
            "Our steel is engineered to withstand extreme tensile loads with certified material grades, precision fabrication, and rigorous testing that guarantees structural integrity under the most demanding conditions.",
        image:
            "/Tensile.jpeg",
        points: [
            "High tensile steel grades",
            "Tensile strength testing",
            "Load-bearing capacity certification",
            "Certified material test reports",
        ],
    },
    {
        number: "01",
        icon: Building2,
        category: "STRUCTURAL STEEL",
        title: "Structural Steel Fabrication",
        shortTitle: "Structural Steel",
        description:
            "Precision-engineered structural steel fabrication for commercial, industrial, and infrastructure projects. We focus on accurate fabrication, strong connections, and dependable structural performance.",
        image:
            "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=format&fit=crop&w=1600&q=85",
        points: [
            "Structural frames & components",
            "Custom steel fabrication",
            "Precision cutting & welding",
            "Project-specific solutions",
        ],
    },
    {
        number: "02",
        icon: Factory,
        category: "INDUSTRIAL STRUCTURES",
        title: "PEB & Industrial Sheds",
        shortTitle: "PEB & Sheds",
        description:
            "Complete steel solutions for warehouses, factories, workshops, and industrial sheds. Our structures are designed with functionality, durability, and efficient project execution in mind.",
        image:
            "https://images.unsplash.com/photo-1565610222536-ef125c59da2e?auto=format&fit=crop&w=1600&q=85",
        points: [
            "Industrial sheds & warehouses",
            "PEB structural components",
            "Roofing & support systems",
            "Durable structural solutions",
        ],
    },
    {
        number: "03",
        icon: Hammer,
        category: "HEAVY FABRICATION",
        title: "Heavy Steel Structures",
        shortTitle: "Heavy Structures",
        description:
            "Heavy-duty steel fabrication for demanding industrial applications where structural strength and reliability are critical. Every component is produced with attention to accuracy and performance.",
        image:
            "https://images.unsplash.com/photo-1565058776037-1c7d7c5b9c09?auto=format&fit=crop&w=1600&q=85",
        points: [
            "Heavy structural components",
            "Industrial platforms",
            "Steel supports & frames",
            "High-strength fabrication",
        ],
    },
    {
        number: "04",
        icon: HardHat,
        category: "SITE EXECUTION",
        title: "Steel Erection & Installation",
        shortTitle: "Erection & Installation",
        description:
            "Professional on-site steel erection and installation services delivered with a strong focus on safety, accurate positioning, efficient execution, and project coordination.",
        image:
            "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1600&q=85",
        points: [
            "Professional steel erection",
            "Safe material handling",
            "Structural installation",
            "On-site project coordination",
        ],
    },
    {
        number: "05",
        icon: Settings,
        category: "CUSTOM SOLUTIONS",
        title: "Customized Fabrication",
        shortTitle: "Custom Fabrication",
        description:
            "Every project comes with different requirements. We provide customized steel fabrication solutions based on your dimensions, applications, technical requirements, and project objectives.",
        image:
            "https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=1600&q=85",
        points: [
            "Project-specific fabrication",
            "Custom dimensions",
            "Technical requirements",
            "Flexible project support",
        ],
    },
    {
        number: "06",
        icon: ShieldCheck,
        category: "QUALITY & SAFETY",
        title: "Quality & Safety",
        shortTitle: "Quality & Safety",
        description:
            "Quality and safety are integrated into every stage of our work. From material selection and fabrication to site execution, we maintain disciplined processes for dependable results.",
        image:
            "https://images.unsplash.com/photo-1531058020387-3be344556be6?auto=format&fit=crop&w=1600&q=85",
        points: [
            "Quality inspection",
            "Safety-focused execution",
            "Material quality checks",
            "Reliable project standards",
        ],
    },
];

// ============================================================
// PROCESS DATA
// ============================================================

const processSteps = [
    {
        number: "01",
        icon: Ruler,
        title: "Understand",
        description:
            "We understand your project requirements, dimensions, application, and technical needs.",
    },
    {
        number: "02",
        icon: Settings,
        title: "Plan",
        description:
            "Our team plans the fabrication and execution process with attention to detail.",
    },
    {
        number: "03",
        icon: Hammer,
        title: "Fabricate",
        description:
            "Materials are processed and fabricated using controlled and quality-focused methods.",
    },
    {
        number: "04",
        icon: Layers3,
        title: "Deliver",
        description:
            "The completed solution is prepared for efficient delivery, erection, and installation.",
    },
];

// ============================================================
// SMALL INLINE ICON
// ============================================================

const UsersIcon = ({ size = 24, className = "" }) => {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
        >
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
    );
};

// ============================================================
// REUSABLE EXPRESSIVE CTA BUTTON
// ============================================================

const CtaButton = ({ label = "Discuss Your Project", className = "" }) => {
    return (
        <button
            type="button"
            className={`group/btn relative mt-7 inline-flex w-fit items-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-slate-900/20 transition-all duration-300 hover:-translate-y-0.5 hover:from-[var(--color-accent)] hover:via-amber-500 hover:to-[var(--color-accent)] hover:shadow-xl hover:shadow-[var(--color-accent)]/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 ${className}`}
        >
            {/* animated shine sweep */}
            <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover/btn:translate-x-full" />

            {/* soft pulsing ring */}
            <span className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-[var(--color-accent)]/0 transition-all duration-300 group-hover/btn:ring-[var(--color-accent)]/40" />

            {/* tiny "clickable" hint dot */}
            <span className="relative flex h-2 w-2 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--color-accent)] opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--color-accent)]" />
            </span>

            <span className="relative z-10">{label}</span>

            <ArrowRight
                size={18}
                className="relative z-10 transition-transform duration-300 group-hover/btn:translate-x-1.5"
            />
        </button>
    );
};

// ============================================================
// SERVICES COMPONENT
// ============================================================

const Services = () => {
    const [activeService, setActiveService] = useState(0);

    const sectionRef = useRef(null);

    const isInView = useInView(sectionRef, {
        once: true,
        margin: "-100px",
    });

    const currentService = services[activeService];

    return (
        <section
            ref={sectionRef}
            className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-50 pt-24 pb-20 sm:pt-28"
        >
            {/* =====================================================
                BACKGROUND
            ===================================================== */}

            <div className="pointer-events-none absolute -right-40 -top-40 h-[500px] w-[500px] rounded-full bg-[var(--color-accent)]/10 blur-3xl" />

            <div className="pointer-events-none absolute -left-40 top-[45%] h-[400px] w-[400px] rounded-full bg-[var(--color-secondary)]/10 blur-3xl" />

            <div className="pointer-events-none absolute bottom-0 right-[20%] h-80 w-80 rounded-full bg-slate-300/10 blur-3xl" />

            <div className="relative z-10 mx-auto max-w-7xl px-6">

                {/* =================================================
                    HERO
                ================================================= */}

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.7 }}
                    className="mx-auto mb-14 max-w-4xl text-center"
                >
                    <span className="pill inline-flex items-center gap-2">
                        <Sparkles size={15} />
                        What We Do
                    </span>

                    <h1 className="mt-5 text-4xl font-bold tracking-tight text-slate-800 sm:text-5xl md:text-6xl">
                        Our{" "}
                        <span className="gradient-text">
                            Services
                        </span>
                    </h1>

                    <p className="mx-auto mt-5 max-w-3xl text-sm leading-7 text-slate-600 sm:text-base md:text-lg">
                        From precision steel fabrication to complete
                        infrastructure solutions, we provide reliable
                        engineering and fabrication services built around
                        your project requirements.
                    </p>

                    <div className="mx-auto mt-6 h-1 w-16 rounded-full bg-[var(--color-accent)]" />
                </motion.div>

                {/* =================================================
                    SERVICE EXPLORER
                ================================================= */}

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{
                        duration: 0.7,
                        delay: 0.2,
                    }}
                    className="overflow-hidden rounded-[32px] border border-slate-200/80 bg-white/80 shadow-xl backdrop-blur-sm"
                >
                    <div className="grid lg:grid-cols-[280px_1fr]">

                        {/* =================================================
                            LEFT SERVICE NAVIGATION
                        ================================================= */}

                        <div className="border-b border-slate-200 bg-slate-50/80 p-4 lg:border-b-0 lg:border-r lg:p-5">
                            <div className="mb-4 px-3 pt-2">
                                <p className="text-[10px] font-bold tracking-[0.2em] text-[var(--color-accent)]">
                                    EXPLORE
                                </p>

                                <p className="mt-1 text-sm font-semibold text-slate-700">
                                    Our Capabilities
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-2 lg:block lg:space-y-1">
                                {services.map((service, index) => {
                                    const Icon = service.icon;
                                    const isActive =
                                        activeService === index;

                                    return (
                                        <button
                                            key={service.number}
                                            type="button"
                                            onClick={() =>
                                                setActiveService(index)
                                            }
                                            className={`group relative flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-all duration-300 ${isActive
                                                ? "bg-slate-900 text-white shadow-md"
                                                : "text-slate-600 hover:bg-white hover:text-slate-900"
                                                }`}
                                        >
                                            {/* Active Indicator */}

                                            {isActive && (
                                                <motion.div
                                                    layoutId="activeService"
                                                    className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-[var(--color-accent)]"
                                                />
                                            )}

                                            {/* Icon */}

                                            <div
                                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-all ${isActive
                                                    ? "bg-white/10"
                                                    : "bg-white group-hover:bg-[var(--color-accent)]/10"
                                                    }`}
                                            >
                                                <Icon
                                                    size={18}
                                                    className={
                                                        isActive
                                                            ? "text-[var(--color-accent)]"
                                                            : "text-slate-500 group-hover:text-[var(--color-accent)]"
                                                    }
                                                />
                                            </div>

                                            {/* Text */}

                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span
                                                        className={`text-[9px] font-bold ${isActive
                                                            ? "text-white/40"
                                                            : "text-slate-300"
                                                            }`}
                                                    >
                                                        {service.number}
                                                    </span>

                                                    <span
                                                        className={`truncate text-xs font-semibold ${isActive
                                                            ? "text-white"
                                                            : "text-slate-700"
                                                            }`}
                                                    >
                                                        {service.shortTitle}
                                                    </span>
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* =================================================
                            FEATURED SERVICE
                        ================================================= */}

                        <div className="min-w-0">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentService.number}
                                    initial={{
                                        opacity: 0,
                                        x: 20,
                                    }}
                                    animate={{
                                        opacity: 1,
                                        x: 0,
                                    }}
                                    exit={{
                                        opacity: 0,
                                        x: -20,
                                    }}
                                    transition={{
                                        duration: 0.35,
                                    }}
                                    className="grid min-h-[520px] md:grid-cols-[0.95fr_1.05fr]"
                                >
                                    {/* =================================================
                                        CONTENT
                                    ================================================= */}

                                    <div className="order-2 flex flex-col justify-center p-7 sm:p-9 md:order-1 md:p-10 lg:p-12">

                                        {/* Number */}

                                        <div className="mb-5 flex items-center gap-3">
                                            <span className="text-5xl font-bold leading-none text-[var(--color-accent)]/15">
                                                {currentService.number}
                                            </span>

                                            <span className="h-px w-8 bg-[var(--color-accent)]/40" />

                                            <span className="text-[10px] font-bold tracking-[0.2em] text-[var(--color-accent)]">
                                                {currentService.category}
                                            </span>
                                        </div>

                                        {/* Icon */}

                                        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--color-accent)]/10">
                                            <currentService.icon
                                                size={24}
                                                className="text-[var(--color-accent)]"
                                            />
                                        </div>

                                        {/* Heading */}

                                        <h2 className="max-w-xl text-3xl font-bold leading-tight text-slate-800 md:text-4xl">
                                            {currentService.title}
                                        </h2>

                                        {/* Description */}

                                        <p className="mt-5 max-w-xl text-sm leading-7 text-slate-600 md:text-[15px]">
                                            {currentService.description}
                                        </p>

                                        {/* Features */}

                                        <div className="mt-6 grid gap-3 sm:grid-cols-2">
                                            {currentService.points.map(
                                                (point) => (
                                                    <div
                                                        key={point}
                                                        className="flex items-start gap-2"
                                                    >
                                                        <CheckCircle2
                                                            size={16}
                                                            className="mt-0.5 shrink-0 text-[var(--color-accent)]"
                                                        />

                                                        <span className="text-xs font-medium leading-5 text-slate-700">
                                                            {point}
                                                        </span>
                                                    </div>
                                                )
                                            )}
                                        </div>

                                        {/* EXPRESSIVE CTA BUTTON */}

                                        <CtaButton label="Discuss Your Project" />
                                    </div>

                                    {/* =================================================
                                        IMAGE
                                    ================================================= */}

                                    <div className="relative order-1 min-h-[300px] overflow-hidden md:order-2 md:min-h-full">

                                        <img
                                            src={currentService.image}
                                            alt={currentService.title}
                                            className="absolute inset-0 h-full w-full object-cover"
                                        />

                                        {/* Overlay */}

                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

                                        {/* Top Badge */}

                                        <div className="absolute right-5 top-5 rounded-full border border-white/20 bg-black/20 px-4 py-2 text-xs font-semibold text-white backdrop-blur-md">
                                            {currentService.number} / 06
                                        </div>

                                        {/* Bottom Info */}

                                        <div className="absolute bottom-6 left-6 right-6">
                                            <div className="rounded-2xl border border-white/20 bg-black/20 p-5 backdrop-blur-md">
                                                <p className="text-[9px] font-bold tracking-[0.2em] text-white/60">
                                                    SANWARIYA JI STEELS
                                                </p>

                                                <p className="mt-2 text-sm font-semibold text-white">
                                                    Engineering • Fabrication • Infrastructure
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </motion.div>

                {/* =================================================
                    QUICK SERVICE INDICATORS
                ================================================= */}

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{
                        opacity: 1,
                        y: 0,
                    }}
                    viewport={{
                        once: true,
                    }}
                    transition={{
                        duration: 0.6,
                    }}
                    className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4"
                >
                    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white/70 px-4 py-4">
                        <Building2
                            size={20}
                            className="text-[var(--color-accent)]"
                        />

                        <div>
                            <p className="text-xs font-bold text-slate-800">
                                100+
                            </p>

                            <p className="text-[10px] text-slate-500">
                                Projects
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white/70 px-4 py-4">
                        <HardHat
                            size={20}
                            className="text-[var(--color-accent)]"
                        />

                        <div>
                            <p className="text-xs font-bold text-slate-800">
                                50+
                            </p>

                            <p className="text-[10px] text-slate-500">
                                Professionals
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white/70 px-4 py-4">
                        <Clock3
                            size={20}
                            className="text-[var(--color-accent)]"
                        />

                        <div>
                            <p className="text-xs font-bold text-slate-800">
                                10+
                            </p>

                            <p className="text-[10px] text-slate-500">
                                Years Experience
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white/70 px-4 py-4">
                        <ShieldCheck
                            size={20}
                            className="text-[var(--color-accent)]"
                        />

                        <div>
                            <p className="text-xs font-bold text-slate-800">
                                100%
                            </p>

                            <p className="text-[10px] text-slate-500">
                                Quality Focus
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* =================================================
                    OUR PROCESS — 2x2 on mobile, 4 in a row on desktop
                ================================================= */}

                <motion.div
                    initial={{
                        opacity: 0,
                        y: 30,
                    }}
                    whileInView={{
                        opacity: 1,
                        y: 0,
                    }}
                    viewport={{
                        once: true,
                        margin: "-100px",
                    }}
                    transition={{
                        duration: 0.6,
                    }}
                    className="mt-20"
                >
                    {/* Heading */}

                    <div className="mx-auto mb-10 max-w-3xl text-center">
                        <span className="text-[10px] font-bold tracking-[0.2em] text-[var(--color-accent)]">
                            HOW WE WORK
                        </span>

                        <h2 className="mt-3 text-3xl font-bold text-slate-800 md:text-4xl">
                            From Requirement to Reality
                        </h2>

                        <p className="mt-4 text-sm leading-7 text-slate-600">
                            A structured approach helps us maintain quality,
                            accuracy, and reliability at every stage of the
                            project.
                        </p>
                    </div>

                    {/* Process — 2 cols on mobile, 4 cols on desktop */}
                    <div className="relative grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4 md:gap-5">

                        {/* Connecting Line (desktop only) */}

                        <div className="pointer-events-none absolute left-[12%] right-[12%] top-10 hidden h-px bg-slate-200 md:block" />

                        {processSteps.map((step, index) => {
                            const Icon = step.icon;

                            return (
                                <motion.div
                                    key={step.number}
                                    initial={{
                                        opacity: 0,
                                        y: 25,
                                    }}
                                    whileInView={{
                                        opacity: 1,
                                        y: 0,
                                    }}
                                    viewport={{
                                        once: true,
                                    }}
                                    transition={{
                                        delay: index * 0.1,
                                        duration: 0.5,
                                    }}
                                    className="relative rounded-2xl border border-slate-200/70 bg-white/80 p-3 text-center shadow-sm sm:p-4 md:p-6"
                                >
                                    {/* Number Circle — smaller on mobile */}

                                    <div className="relative z-10 mx-auto flex h-12 w-12 items-center justify-center rounded-full border-4 border-slate-50 bg-white shadow-sm sm:h-16 sm:w-16 sm:border-[6px] md:h-20 md:w-20 md:border-8">
                                        <Icon
                                            size={20}
                                            className="text-[var(--color-accent)] sm:h-6 sm:w-6 md:h-[25px] md:w-[25px]"
                                        />
                                    </div>

                                    <span className="mt-3 block text-[9px] font-bold tracking-[0.2em] text-[var(--color-accent)] sm:mt-4 sm:text-[10px] md:mt-5">
                                        STEP {step.number}
                                    </span>

                                    <h3 className="mt-1 text-sm font-bold text-slate-800 sm:mt-2 sm:text-base md:text-lg">
                                        {step.title}
                                    </h3>

                                    <p className="mt-1 text-[10px] leading-5 text-slate-600 sm:mt-2 sm:text-xs sm:leading-6">
                                        {step.description}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>
                </motion.div>

                {/* =================================================
                    WHY US
                ================================================= */}

                <motion.div
                    initial={{
                        opacity: 0,
                        y: 30,
                    }}
                    whileInView={{
                        opacity: 1,
                        y: 0,
                    }}
                    viewport={{
                        once: true,
                        margin: "-100px",
                    }}
                    transition={{
                        duration: 0.6,
                    }}
                    className="mt-20 overflow-hidden rounded-[30px] bg-slate-900 p-8 md:p-10 lg:p-12"
                >
                    <div className="grid items-center gap-10 lg:grid-cols-[0.9fr_1.1fr]">

                        {/* Left */}

                        <div>
                            <span className="text-[10px] font-bold tracking-[0.2em] text-[var(--color-accent)]">
                                WHY CHOOSE US
                            </span>

                            <h2 className="mt-3 text-3xl font-bold leading-tight text-white md:text-4xl">
                                More Than Fabrication.
                                <br />
                                We Build Reliability.
                            </h2>

                            <p className="mt-5 max-w-xl text-sm leading-7 text-slate-300">
                                We combine technical knowledge, experienced
                                professionals, quality-focused processes,
                                and practical project execution to deliver
                                steel solutions our clients can depend on.
                            </p>

                            {/* EXPRESSIVE CTA BUTTON (light variant) */}
                            <button
                                type="button"
                                className="group/btn relative mt-7 inline-flex w-fit items-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-[var(--color-accent)] via-amber-400 to-[var(--color-accent)] px-6 py-3.5 text-sm font-bold text-slate-900 shadow-lg shadow-[var(--color-accent)]/40 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[var(--color-accent)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                            >
                                {/* shine sweep */}
                                <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/50 to-transparent transition-transform duration-700 group-hover/btn:translate-x-full" />

                                {/* pulsing dot */}
                                <span className="relative flex h-2 w-2 shrink-0">
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-slate-900 opacity-60" />
                                    <span className="relative inline-flex h-2 w-2 rounded-full bg-slate-900" />
                                </span>

                                <span className="relative z-10">Talk To Our Team</span>

                                <ArrowRight
                                    size={18}
                                    className="relative z-10 transition-transform duration-300 group-hover/btn:translate-x-1.5"
                                />
                            </button>
                        </div>

                        {/* Right */}

                        <div className="grid gap-3 sm:grid-cols-2">

                            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                                <ShieldCheck
                                    size={24}
                                    className="text-[var(--color-accent)]"
                                />

                                <h3 className="mt-4 text-base font-bold text-white">
                                    Quality Focused
                                </h3>

                                <p className="mt-2 text-xs leading-6 text-slate-400">
                                    Quality control remains an important part
                                    of our fabrication and execution process.
                                </p>
                            </div>

                            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                                <Settings
                                    size={24}
                                    className="text-[var(--color-accent)]"
                                />

                                <h3 className="mt-4 text-base font-bold text-white">
                                    Technical Expertise
                                </h3>

                                <p className="mt-2 text-xs leading-6 text-slate-400">
                                    Experienced professionals work with
                                    practical engineering knowledge.
                                </p>
                            </div>

                            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                                <HardHat
                                    size={24}
                                    className="text-[var(--color-accent)]"
                                />

                                <h3 className="mt-4 text-base font-bold text-white">
                                    Safety First
                                </h3>

                                <p className="mt-2 text-xs leading-6 text-slate-400">
                                    We maintain a safety-focused approach
                                    during workshop and site operations.
                                </p>
                            </div>

                            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                                <UsersIcon
                                    size={24}
                                    className="text-[var(--color-accent)]"
                                />

                                <h3 className="mt-4 text-base font-bold text-white">
                                    Client Focus
                                </h3>

                                <p className="mt-2 text-xs leading-6 text-slate-400">
                                    Clear communication and dependable support
                                    throughout the project.
                                </p>
                            </div>

                        </div>
                    </div>
                </motion.div>

                {/* =================================================
                    FINAL CTA
                ================================================= */}


            </div>
        </section>
    );
};

export default Services;